import { Checkbox, Form, InputNumber, Skeleton, TimePicker } from 'antd'
import dayjs, { type Dayjs } from 'dayjs'
import { observer } from 'mobx-react-lite'
import { useCallback, useEffect, useState } from 'react'

import { restClient } from '@/Core/Services/RestClientService'
import { FormActionsBar } from '@/Ship/Components/UI/FormActionsBar'
import shell from '@/Ship/Components/UI/SettingsShell/SettingsShell.module.css'
import { cx } from '@/Ship/Helpers/cssClass'
import { notify } from '@/Ship/Helpers/notify'
import {
    DEFAULT_SHIFT_REMINDER_OPTIONS,
    MAX_SHIFT_REMINDER_MIN_HOURS,
    OPTION_SHIFT_REMINDER_ENABLED,
    OPTION_SHIFT_REMINDER_MIN_HOURS,
    OPTION_SHIFT_REMINDER_TIME,
    parseReminderTime,
    readShiftReminderOptions,
    type ShiftReminderOptionsForm,
} from '@/Ship/Helpers/portalOptions'

/**
 * Розділ «Нагадування про зміну»: пуш водієві, який не завершив розпочату зміну, — раз на добу
 * в заданий час (пропущений час досилається до кінця доби). Свіжі зміни (розпочаті менше ніж за
 * N годин до нагадування) сьогодні не нагадуються — лише наступної доби.
 */

const CODES = [OPTION_SHIFT_REMINDER_ENABLED, OPTION_SHIFT_REMINDER_TIME, OPTION_SHIFT_REMINDER_MIN_HOURS] as const

/** Форма тримає час рядком «HH:mm», TimePicker — dayjs */
const toPicker = (value: unknown) => {
    const time = parseReminderTime(value)
    if (!time) return { value: null }
    const [hour = 0, minute = 0] = time.split(':').map(Number)
    return { value: dayjs().hour(hour).minute(minute).second(0) }
}
const fromPicker = (value: Dayjs | null) => (value ? value.format('HH:mm') : undefined)

export const ShiftReminderSection = observer(() => {
    const [form] = Form.useForm<ShiftReminderOptionsForm>()
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [baseline, setBaseline] = useState<ShiftReminderOptionsForm | null>(null)
    const [values, setValues] = useState<ShiftReminderOptionsForm | null>(null)
    const dirty = baseline !== null && values !== null && CODES.some((code) => values[code] !== baseline[code])
    const enabled = values?.[OPTION_SHIFT_REMINDER_ENABLED] ?? false

    const load = useCallback(async () => {
        setLoading(true)
        try {
            const result: any = await restClient.get('/services/tenant/options', { searchParams: { limit: 300 } })
            const initial = readShiftReminderOptions(result?.items ?? [])
            setBaseline(initial)
            setValues(initial)
        } catch {
            notify.error('Не вдалося завантажити налаштування')
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        void load()
    }, [load])

    const handleReset = () => {
        if (!baseline) return
        form.setFieldsValue(baseline)
        setValues(baseline)
    }

    const handleSave = async (formValues: ShiftReminderOptionsForm) => {
        const next: ShiftReminderOptionsForm = {
            [OPTION_SHIFT_REMINDER_ENABLED]: !!formValues[OPTION_SHIFT_REMINDER_ENABLED],
            [OPTION_SHIFT_REMINDER_TIME]: formValues[OPTION_SHIFT_REMINDER_TIME],
            [OPTION_SHIFT_REMINDER_MIN_HOURS]: Number(formValues[OPTION_SHIFT_REMINDER_MIN_HOURS]),
        }
        setSaving(true)
        try {
            await Promise.all(
                CODES.map((code) => restClient.post('/services/tenant/options', { json: { code, value: next[code] } }))
            )
            setBaseline(next)
            notify.success('Налаштування збережено')
        } catch {
            notify.error('Помилка збереження налаштувань')
        } finally {
            setSaving(false)
        }
    }

    const head = (
        <div className={cx(shell, 'sectionHead')}>
            <h2 className={cx(shell, 'sectionTitle')}>Нагадування про зміну</h2>
            <p className={cx(shell, 'sectionHint')}>
                Водій, який не завершив розпочату зміну, раз на добу отримує сповіщення в кабінеті водія з проханням її
                завершити.
            </p>
        </div>
    )

    if (loading || !baseline) {
        return (
            <div className={cx(shell, 'section')}>
                {head}
                <Skeleton active paragraph={{ rows: 3 }} />
            </div>
        )
    }

    return (
        <Form
            form={form}
            layout="vertical"
            initialValues={baseline}
            onValuesChange={(_, all) => setValues(all)}
            onFinish={handleSave}
        >
            <div className={cx(shell, 'stack')}>
                <div className={cx(shell, 'section')}>
                    {head}
                    <Form.Item
                        name={OPTION_SHIFT_REMINDER_ENABLED}
                        valuePropName="checked"
                        extra={enabled ? 'Сповіщення надсилаються щодня в заданий час' : 'Сповіщення не надсилаються'}
                    >
                        <Checkbox data-testid="shift-reminder-enabled">Нагадувати завершити зміну</Checkbox>
                    </Form.Item>
                    <Form.Item
                        name={OPTION_SHIFT_REMINDER_TIME}
                        label="Час нагадування"
                        rules={[{ required: true, message: 'Обовʼязкове поле' }]}
                        getValueProps={toPicker}
                        normalize={fromPicker}
                        extra={`За київським часом. Якщо в цей час сервер не працював, нагадування надійде пізніше, але того ж дня. За замовчуванням — ${DEFAULT_SHIFT_REMINDER_OPTIONS[OPTION_SHIFT_REMINDER_TIME]}`}
                    >
                        <TimePicker
                            format="HH:mm"
                            minuteStep={5}
                            allowClear={false}
                            needConfirm={false}
                            style={{ width: 200 }}
                            data-testid="shift-reminder-time"
                        />
                    </Form.Item>
                    <Form.Item
                        name={OPTION_SHIFT_REMINDER_MIN_HOURS}
                        label="Не нагадувати про зміни коротші за, год"
                        rules={[{ required: true, message: 'Обовʼязкове поле' }]}
                        extra={`Зміна, розпочата пізніше ніж за стільки годин до нагадування (вечірня чи нічна), сьогодні не нагадується — лише наступного дня. За замовчуванням — ${DEFAULT_SHIFT_REMINDER_OPTIONS[OPTION_SHIFT_REMINDER_MIN_HOURS]} год`}
                        style={{ marginBottom: 0 }}
                    >
                        <InputNumber
                            min={0}
                            max={MAX_SHIFT_REMINDER_MIN_HOURS}
                            step={1}
                            precision={0}
                            style={{ width: 200 }}
                            data-testid="shift-reminder-min-hours"
                        />
                    </Form.Item>
                </div>

                <div className={cx(shell, 'actions')}>
                    <FormActionsBar dirty={dirty} saving={saving} onReset={handleReset} />
                </div>
            </div>
        </Form>
    )
})
